/**
 * Журнал сроков обучения (canon service-training «Журнал сроков»). Единый компонент для двух
 * контуров: клиентский (свои сотрудники) и командный пульт (по выбранному клиенту). Светофор-
 * счётчики по людям («Просрочено» и «Истекает скоро» независимы: сотрудник с просрочкой по одной
 * программе и истекающим сроком по другой учтён в обоих), колонки по профилю (из ответа `columns`), строки-сотрудники через TrafficRow
 * (клетки направлений + «+N» для ролевых, цвет строки = rowStatus, раскрытие = записи). Фильтры
 * (ФИО/статус/направление/подразделение), группировка по подразделениям, серая строка «Нет данных»,
 * выгрузка в Excel (подробно по событиям). «Напомнить» — только команде (ручной триггер, Блок 7).
 *
 * F2-16: над строками — TrafficRowHeader («Сотрудник» + направления из `data.columns` + «Прочее»
 * при наличии скрытых), выровненный с чипами строк по общей сетке (см. traffic-row.tsx). Заголовки
 * НЕ зашиты — порядок/состав колонок целиком из ответа сервера.
 */
import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Bell, Download, GraduationCap } from 'lucide-react'
import type { JournalResponse, JournalRow, TrainingDirection } from '@/lib/api'
import { formatOtIndices, shortTerm, trainingStatusToVariant } from '@/lib/training'
import { backState } from '@/lib/use-back-target'
import { exportTrainingJournalToExcel } from '@/lib/excel'
import { getErrorMessage } from '@/lib/errors'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { StatCard } from '@/design-system/components/data/stat-card'
import { StatGrid } from '@/design-system/components/data/stat-grid'
import {
  TrafficRow,
  TrafficRowHeader,
  type TrafficDirection,
} from '@/design-system/components/data/traffic-row'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const ALL = 'all'

/** Фильтр по статусу строки → цвет строки (rowStatus). */
const STATUS_FILTER_TO_ROW: Record<string, 'red' | 'amber' | 'green' | 'gray'> = {
  overdue: 'red',
  soon: 'amber',
  ok: 'green',
  none: 'gray',
}

/**
 * Подходит ли строка под фильтр по статусу. «Истекает скоро» отбирает по признаку счётчика — у
 * сотрудника есть истекающее обучение, даже если строка красная из-за просрочки по другой
 * программе (зеркало серверного `rowMatchesStatusColor`); остальные значения — по цвету строки.
 */
function matchesStatusFilter(row: JournalRow, status: string): boolean {
  if (status === 'soon') return row.records.some((rec) => rec.status === 'expiring')
  return row.rowStatus === STATUS_FILTER_TO_ROW[status]
}

/** Клетка направления → чип TrafficRow (для свёрнутого вида). */
function cellToDirection(cell: JournalRow['cells'][number]): TrafficDirection {
  return {
    name: cell.directionLabel,
    idx: cell.directionKey === 'ot' && cell.otIndices?.length ? formatOtIndices(cell.otIndices) : undefined,
    status: trainingStatusToVariant(cell.status),
    term: cell.term,
  }
}

/**
 * Запись обучения → строка раскрытия TrafficRow (ОТ — одной строкой с чипами-индексами).
 * В детали выводим АКТУАЛЬНОЕ наименование направления рядом с программой — иначе
 * пользовательские направления, свёрнутые в колонку «Прочее», не различить (centrika-docs!55).
 */
function recordToDirection(record: JournalRow['records'][number]): TrafficDirection {
  const idx =
    record.directionSystemCode === 'ot' && record.otIndices.length
      ? formatOtIndices(record.otIndices)
      : undefined
  return {
    name: `${record.directionName} · ${record.programName}`,
    idx,
    status: trainingStatusToVariant(record.status),
    term: shortTerm({ status: record.status, dueOn: record.dueOn, recurrenceMode: record.recurrenceMode }),
  }
}

function RemindButton({
  personId,
  onRemind,
}: {
  personId: string
  onRemind: (personId: string) => Promise<void>
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle')
  const [error, setError] = useState<string | null>(null)

  async function click() {
    setError(null)
    setState('busy')
    try {
      await onRemind(personId)
      setState('done')
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось отправить напоминание'))
      setState('idle')
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        disabled={state === 'busy'}
        onClick={() => void click()}
        leftIcon={<Bell className="size-4 stroke-current" aria-hidden />}
      >
        {state === 'busy' ? 'Отправка…' : state === 'done' ? 'Напоминание отправлено' : 'Напомнить'}
      </Button>
      {error ? <span className="text-12 text-red">{error}</span> : null}
    </div>
  )
}

export interface TrainingJournalProps {
  data: JournalResponse
  /** Команде доступен ручной триггер «Напомнить»; клиенту — нет. */
  canRemind?: boolean
  onRemind?: (personId: string, direction?: TrainingDirection) => Promise<void>
  /** Суффикс имени файла выгрузки (например, имя клиента у команды). */
  exportSuffix?: string
  /**
   * Ссылка на карточку сотрудника по строке журнала (MR !17). Задаётся контуром, у которого такая
   * страница есть: клиент — `/staff/:id`. Без неё ФИО остаётся текстом.
   */
  employeeHref?: (personId: string) => string
}

export function TrainingJournal({
  data,
  canRemind,
  onRemind,
  exportSuffix,
  employeeHref,
}: TrainingJournalProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [department, setDepartment] = useState(ALL)
  const [status, setStatus] = useState(ALL)
  const [direction, setDirection] = useState(ALL)
  const [search, setSearch] = useState('')

  const departments = useMemo(() => {
    const set = new Set<string>()
    for (const r of data.rows) set.add(r.departmentName || 'Без подразделения')
    return Array.from(set)
  }, [data.rows])

  // Опции направления — колонки профиля + «Прочее» (ролевые).
  const directionOptions = useMemo<{ key: string; label: string }[]>(
    () => [...data.columns.map((c) => ({ key: c.key as string, label: c.label })), { key: 'other', label: 'Прочее' }],
    [data.columns],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return data.rows.filter((r) => {
      const dep = r.departmentName || 'Без подразделения'
      if (department !== ALL && dep !== department) return false
      if (status !== ALL && !matchesStatusFilter(r, status)) return false
      if (q && !r.fullName.toLowerCase().includes(q)) return false
      // Фильтр по направлению сверяет system_code записи: значение «Прочее» (other) отбирает
      // только записи самого системного «Прочего»; пользовательские направления (system_code
      // пуст) под него не подпадают — сохраняют собственное имя (centrika-docs!55).
      if (direction !== ALL && !r.records.some((rec) => rec.directionSystemCode === direction)) return false
      return true
    })
  }, [data.rows, department, status, direction, search])

  // Группировка отфильтрованных строк по подразделениям (с сохранением порядка).
  const groups = useMemo(() => {
    const list: Array<{ department: string; items: JournalRow[] }> = []
    const idx = new Map<string, number>()
    for (const r of filtered) {
      const dep = r.departmentName || 'Без подразделения'
      if (!idx.has(dep)) {
        idx.set(dep, list.length)
        list.push({ department: dep, items: [] })
      }
      list[idx.get(dep)!].items.push(r)
    }
    return list
  }, [filtered])

  const visibleCount = Math.max(1, data.columns.length)

  // F2-16: колонка «Прочее» в заголовке появляется, только если среди ОТОБРАЖАЕМЫХ строк есть
  // скрытые (ролевые) направления — иначе пустая колонка в шапке была бы лишней.
  const hasOtherColumn = useMemo(() => filtered.some((r) => r.other.count > 0), [filtered])

  return (
    <div>
      <StatGrid className="mb-5">
        <StatCard label="Просрочено" value={data.counters.overdue} status="danger" />
        <StatCard label="Истекает скоро" value={data.counters.soon} status="warning" />
        <StatCard label="Всё в порядке" value={data.counters.ok} status="success" />
        <StatCard label="Нет данных" value={data.counters.noData} noData />
      </StatGrid>

      <Toolbar>
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger aria-label="Фильтр по подразделению" className="w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все подразделения</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d} value={d}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger aria-label="Фильтр по статусу" className="w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Любой статус</SelectItem>
            <SelectItem value="overdue">Просрочено</SelectItem>
            <SelectItem value="soon">Истекает скоро</SelectItem>
            <SelectItem value="ok">Всё в порядке</SelectItem>
            <SelectItem value="none">Нет данных</SelectItem>
          </SelectContent>
        </Select>
        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger aria-label="Фильтр по направлению" className="w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все направления</SelectItem>
            {directionOptions.map((o) => (
              <SelectItem key={o.key} value={o.key}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по ФИО"
          className="max-w-narrow"
          aria-label="Поиск по ФИО"
        />
        <Button
          variant="secondary"
          disabled={filtered.length === 0}
          onClick={() => exportTrainingJournalToExcel(filtered, exportSuffix ?? 'journal')}
          leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
        >
          Выгрузить в Excel
        </Button>
      </Toolbar>

      {filtered.length === 0 ? (
        <Panel>
          <EmptyState
            icon={GraduationCap}
            title="Сотрудников не найдено"
            description="Измените фильтр — журнал наполняется из заказов Центрики и внешних записей"
          />
        </Panel>
      ) : (
        <Panel className="overflow-hidden">
          {/* Горизонтальная прокрутка на узком экране (F2-16): заголовок и строки — общий скролл-
              контейнер. min-w-fit не даёт колонкам сжаться уже их минимумов, но и не раздвигает
              таблицу шире экрана, пока минимумы помещаются (min-w-max растягивал бы гибкие
              колонки под самое длинное ФИО). */}
          <div className="overflow-x-auto">
            <div className="min-w-fit">
              <TrafficRowHeader columns={data.columns} showOtherColumn={hasOtherColumn} />
              {groups.map((g) => (
                <div key={g.department}>
                  <div className="border-b border-line bg-group px-4 py-2 text-12 font-semibold uppercase text-txt-tertiary">
                    {g.department}
                  </div>
                  {g.items.map((row) => (
                    <TrafficRow
                      key={row.personId}
                      employee={row.fullName}
                      employeeHref={employeeHref?.(row.personId)}
                      onEmployeeClick={
                        employeeHref
                          ? (e) => {
                              // SPA-переход: полная перезагрузка страницы не нужна, но обычный
                              // href сохраняется (средний клик/новая вкладка работают штатно).
                              // `state.from` — чтобы «Назад» в карточке сотрудника вернул в журнал,
                              // а не в каталог «Сотрудники» (общий паттерн, см. use-back-target).
                              e.preventDefault()
                              navigate(employeeHref(row.personId), { state: backState(location) })
                            }
                          : undefined
                      }
                      sub={row.position}
                      directions={row.cells.map(cellToDirection)}
                      visibleCount={visibleCount}
                      rowStatus={row.rowStatus}
                      showOtherColumn={hasOtherColumn}
                      extra={
                        row.other.count > 0
                          ? { count: row.other.count, status: row.other.status }
                          : undefined
                      }
                      details={row.records.map(recordToDirection)}
                      footer={
                        // Кнопка «Напомнить» — только если у сотрудника есть хотя бы одна запись
                        // по программе, режим которой НЕ «определяет работодатель» (centrika-docs!54).
                        canRemind &&
                        onRemind &&
                        row.records.some((r) => r.recurrenceMode !== 'employer') ? (
                          <RemindButton personId={row.personId} onRemind={onRemind} />
                        ) : undefined
                      }
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Panel>
      )}
    </div>
  )
}
